import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, MapPin, ShieldCheck, BadgeCheck, Link2, Landmark,
  Home, User, DollarSign, Megaphone, CheckCircle2,
} from "lucide-react";
import SocialLinks from "@/components/SocialLinks";
import { SignInModal } from "@/components/SignInModal";
import PwaInstallModal from "@/components/PwaInstallModal";
import { SafeImg } from "@/components/SafeImg";
import { SEED_DEALS, getTrustBadge } from "@/features/snaggedify/dealFeedData";

const NAV_LINKS = [{ id: "why", label: "Why Snaggedify" }, { id: "about", label: "About Us" }];

// Above-the-fold "Live Deal Feed" preview — real card data/verdict badges, not a
// decorative mock, so a first-time visitor sees the actual trust-verdict product
// before ever clicking Start Scrolling.
const HERO_PREVIEW_IDS = ["item_101", "item_102", "item_104"];
const HERO_PREVIEW_DEALS = HERO_PREVIEW_IDS.map((id) => SEED_DEALS.find((d) => d.id === id)).filter(Boolean);

const STAT_CARDS = [
  {
    id: "search-scams",
    icon: DollarSign,
    title: "Social & Search Scams",
    body: "According to FTC data, online shopping scam losses reached $2.1 billion, with the majority originating from targeted search engine and social media ads.",
  },
  {
    id: "spoofing",
    icon: Megaphone,
    title: "Search Engine Spoofing",
    body: "Over 40% of consumers who lost money to online shopping scams reported buying from paid ad slots that impersonated popular brands.",
  },
];

const BADGES = [
  { id: "chicago", icon: MapPin, label: "Built in Chicago, IL, USA" },
  { id: "rmc", icon: ShieldCheck, label: "RMC Tech" },
  { id: "black-owned", icon: BadgeCheck, label: "Black-Owned Business", accent: true },
  { id: "domestic", icon: Landmark, label: "US Domestic Resellers Only" },
];

export default function Landing() {
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [pwaOpen, setPwaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const handleAuth = () => setAuthOpen(true);
  const startScrolling = () => navigate("/app");
  const checkALink = () => {
    try { sessionStorage.setItem("snaggedify_launch_tab", "check"); } catch { /* ignore */ }
    navigate("/app");
  };
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen scroll-smooth bg-white text-slate-900 flex flex-col" data-testid="landing-page">
      {/* Sticky glass header */}
      <header
        className={`sticky top-0 z-40 border-b transition-all duration-300 ${scrolled ? "border-slate-200 bg-white/90 backdrop-blur-xl shadow-sm" : "border-transparent bg-white/50 backdrop-blur-md"}`}
        id="top"
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-6 h-16">
          <a href="#top" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/slip-logo.png" alt="Snaggedify" className="h-9 w-9" />
            <span className="flex flex-col leading-none">
              <span className="whitespace-nowrap font-black tracking-tighter" style={{ fontSize: "clamp(1rem, 4vw, 1.25rem)" }}>Snaggedify</span>
              <span className="hidden text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 sm:inline">by Cheapify</span>
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-6" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <button key={l.id} onClick={() => scrollTo(l.id)} data-testid={`nav-${l.id}`} className="text-sm font-semibold text-slate-600 transition-colors hover:text-brand-orange">{l.label}</button>
            ))}
            <button onClick={startScrolling} data-testid="nav-start-scrolling-btn" className="rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-5 h-10 text-xs font-black uppercase tracking-[0.08em] text-slate-950 transition-transform hover:scale-105">Start Scrolling</button>
            <button onClick={handleAuth} data-testid="nav-signin-btn" className="rounded-full border border-slate-300 px-5 h-10 text-xs font-black uppercase tracking-[0.08em] text-slate-700 transition-colors hover:border-brand-orange hover:text-brand-orange">Sign In</button>
          </nav>
          <div className="flex items-center gap-2 md:hidden">
            <button onClick={startScrolling} data-testid="mobile-start-scrolling-btn" className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-3 h-9 text-[11px] font-black uppercase tracking-wide text-slate-950">
              <Sparkles className="h-3.5 w-3.5" /> Start Scrolling
            </button>
            <button onClick={handleAuth} data-testid="nav-signin-mobile" aria-label="Sign In" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-700"><User className="h-4.5 w-4.5" /></button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden bg-white" data-testid="landing-hero">
          <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(255,115,0,0.12), transparent 70%)" }} />
          <div className="relative mx-auto flex min-h-[calc(100dvh-4rem)] lg:min-h-0 max-w-5xl flex-col justify-center px-6 py-5 lg:py-20">
            <div className="grid gap-6 lg:grid-cols-2 lg:items-center">
              <div className="text-center lg:text-left">
                <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2 rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-orange">
                  <Sparkles className="h-3.5 w-3.5" /> Snaggedify by Cheapify
                </span>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tighter text-slate-900" data-testid="hero-tagline">
                  Craigslist for resellers — but every one is verified.
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600 lg:mx-0" data-testid="hero-subtext">
                  We vet every reseller before they're listed. You see who's legit, who's a scam.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                  <button onClick={startScrolling} data-testid="hero-start-scrolling-btn" className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-8 h-14 text-base font-black uppercase tracking-[0.06em] text-slate-950 shadow-[0_10px_40px_-10px_rgba(255,115,0,0.55)] transition-transform hover:scale-105">
                    Start Scrolling <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </button>
                  <button onClick={checkALink} data-testid="hero-check-link-btn" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 h-14 text-base font-black uppercase tracking-[0.06em] text-slate-700 transition-colors hover:border-brand-orange hover:text-brand-orange">
                    <Link2 className="h-4.5 w-4.5" /> Check a Link
                  </button>
                </div>
              </div>
              <div className="relative mx-auto flex w-full max-w-[300px] flex-col gap-2.5" data-testid="hero-live-feed-preview">
                <p className="mb-1 flex items-center justify-center gap-1.5 text-center text-[11px] font-black uppercase tracking-[0.14em] text-slate-400 lg:justify-start lg:text-left">
                  <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
                  Live Deal Feed
                </p>
                {HERO_PREVIEW_DEALS.map((deal, i) => {
                  const badge = getTrustBadge(deal.verdict);
                  return (
                    <div key={deal.id} data-testid={`hero-preview-card-${deal.id}`} className={`flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-2.5 shadow-md ${i === 1 ? "lg:translate-x-3" : ""}`}>
                      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-slate-100"><SafeImg src={deal.image} alt={deal.title} className="h-full w-full object-cover" /></div>
                      <div className="min-w-0 flex-1">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[9px] font-black uppercase tracking-wide ${badge.className}`}><badge.Icon className="h-3 w-3" strokeWidth={3} /> {badge.label}</span>
                        <p className="mt-1 truncate text-xs font-bold text-slate-900">{deal.title}</p>
                        <p className="text-xs font-black text-brand-orange">${deal.price.toFixed(2)} <span className="text-[10px] font-bold text-slate-400 line-through">${deal.msrp.toFixed(2)}</span></p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* WHY NOT JUST GOOGLE IT */}
        <section id="why" className="scroll-mt-20 border-y border-slate-100 bg-slate-50/60 px-6 py-20" data-testid="why-google-section">
          <div className="mx-auto max-w-6xl">
            <h2 className="text-center text-3xl sm:text-4xl font-black tracking-tighter text-slate-900">Why Not Just Google It?</h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-slate-500">Because the top result isn't always the safest one.</p>
            <div className="mt-10 grid gap-5 md:grid-cols-2">
              {STAT_CARDS.map((c) => (
                <div key={c.id} className="rounded-2xl border border-rose-200 bg-white p-6 shadow-sm" data-testid={`stat-card-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50"><c.icon className="h-5 w-5 text-rose-500" strokeWidth={2.25} /></div>
                    <p className="text-base font-black tracking-tight text-slate-900">{c.title}</p>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600">{c.body}</p>
                </div>
              ))}
            </div>

            <div className="mx-auto mt-6 max-w-4xl rounded-2xl border-2 border-brand-orange/40 bg-gradient-to-br from-amber-50 to-orange-50 p-6 shadow-sm" data-testid="snaggedify-differentiator">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-orange/15"><CheckCircle2 className="h-5 w-5 text-brand-orange" strokeWidth={2.25} /></div>
                <p className="text-base font-black tracking-tight text-slate-900">The Snaggedify Differentiator</p>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-slate-700">
                Search engines index paid ads by budget—not by seller safety. Every reseller on Snaggedify is manually vetted US domestic, with live price math and zero paid search clutter.
              </p>
            </div>
          </div>
        </section>

        {/* FOUNDER / ABOUT */}
        <section id="about" className="scroll-mt-20 border-y border-slate-100 bg-slate-50/60 px-6 py-20" data-testid="about-section">
          <div className="mx-auto max-w-3xl px-0 text-center">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter text-slate-900" data-testid="about-headline">Built to Stop the Ripoffs</h2>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600" data-testid="about-story">
              Built out of Chicago, Illinois by Tyler at RMC Tech, Snaggedify was created for one simple reason: so everyday shoppers stop getting ripped off by shady resellers and online scam ads. We believe you should be able to hunt for great deals and live freely without worrying about getting scammed. That's why we manually vet US domestic resellers, show you side-by-side original retail price math, and keep our platform 100% ad-free.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              {BADGES.map((b) => (
                <span key={b.id} data-testid={`badge-${b.id}`} className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs font-bold ${b.accent ? "border-brand-orange/30 bg-brand-orange/10 text-brand-orange" : "border-slate-200 bg-white text-slate-700"}`}>
                  <b.icon className="h-4 w-4 text-brand-orange" /> {b.label}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="mx-auto max-w-3xl px-6 py-20 text-center" data-testid="final-cta-section">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter text-slate-900">Craigslist for resellers — but every one is verified.</h2>
          <button onClick={startScrolling} data-testid="final-cta-btn" className="group mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-yellow to-brand-orange px-10 h-14 text-base font-black uppercase tracking-[0.06em] text-slate-950 shadow-[0_10px_40px_-10px_rgba(255,115,0,0.55)] transition-transform hover:scale-105">Start Scrolling <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" /></button>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white" data-testid="landing-footer">
        <div className="mx-auto max-w-6xl px-6 pt-10 pb-24">
          <div className="mb-8 flex flex-col items-center justify-center gap-2 rounded-2xl border border-brand-orange/30 bg-brand-orange/[0.06] px-6 py-5 text-center sm:flex-row" data-testid="early-access-footer">
            <Sparkles className="h-5 w-5 text-brand-orange" />
            <p className="text-sm font-bold text-brand-orange"><span className="text-base font-black uppercase tracking-[0.12em]">Early Access · Alpha</span> — Snaggedify is brand new. You may encounter minor bugs — report any issues via the app's Profile → Report a Bug.</p>
          </div>
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="flex items-center gap-2"><img src="/slip-logo.png" alt="Snaggedify" className="h-8 w-8" /><span className="text-lg font-bold tracking-tighter text-slate-900">Snaggedify by Cheapify</span></div>
            <p className="max-w-md text-sm font-semibold text-slate-500" data-testid="footer-tagline">The reseller marketplace where every seller is verified — so you never get ripped off.</p>
            <button onClick={() => scrollTo("about")} data-testid="footer-about" className="text-sm font-medium text-slate-500 transition-colors hover:text-brand-orange">About Us</button>
            <button onClick={() => setPwaOpen(true)} data-testid="footer-pwa-install-link" className="text-sm font-medium text-slate-500 transition-colors hover:text-brand-orange">Add Snaggedify to your Home Screen</button>
            <p className="mt-2 text-xs text-slate-400">Built by RMC Tech · Chicago, IL, USA · A Black-owned business by Tyler</p>
            <SocialLinks variant="light" className="mt-3" />
            <p className="mx-auto mt-6 max-w-2xl text-[11px] leading-relaxed text-slate-400" data-testid="ftc-disclosure">
              <span className="font-bold text-slate-500">How Snaggedify works:</span> Snaggedify manually vets resellers and organizes verified deal listings from third-party sellers. We may earn a small commission if you buy through an outbound link, at zero extra cost to you. As an Amazon Associate, we earn from qualifying purchases. Prices, availability, taxes, shipping, and final checkout totals may change at the retailer.
            </p>
            <p className="mt-2 text-[11px] text-slate-400">© 2026 Snaggedify by Cheapify. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <SignInModal open={authOpen} onClose={() => setAuthOpen(false)} />
      <PwaInstallModal open={pwaOpen} onClose={() => setPwaOpen(false)} />

      {/* Mobile bottom navigation */}
      <nav className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-4 border-t border-slate-200 bg-white/95 backdrop-blur-md md:hidden" data-testid="landing-bottom-nav">
        {[
          { id: "top", label: "Home", icon: Home, onClick: () => scrollTo("top") },
          { id: "why", label: "Why Us", icon: ShieldCheck, onClick: () => scrollTo("why") },
          { id: "install", label: "Get App", icon: Sparkles, onClick: () => setPwaOpen(true) },
          { id: "account", label: "Sign In", icon: User, onClick: handleAuth },
        ].map((it) => (
          <button key={it.id} onClick={it.onClick} data-testid={`bottom-nav-${it.id}`} className="flex flex-col items-center gap-1 py-2.5 text-[10px] font-bold text-slate-500 transition-colors hover:text-brand-orange">
            <it.icon className="h-5 w-5" strokeWidth={2.25} />{it.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
